import React, { useCallback, useMemo } from 'react'

import { Select } from 'govwell-ui'
import { SelectOption } from 'govwell-ui/components/Select/types'

import { useTaskTemplateFieldsForCustomColumnQuery } from 'src/fetch/taskTemplates'
import { useFieldContext } from 'src/hooks/use-app-form'

type TaskFieldOption = {
  fieldId: number
  label: string
}

type Props = {
  onFieldSelected?: (fieldLabel: string) => void
}

const TaskFieldSelectField = ({ onFieldSelected }: Props) => {
  const field = useFieldContext<number[]>()
  const { data, isLoading } = useTaskTemplateFieldsForCustomColumnQuery()

  const options: SelectOption<TaskFieldOption>[] = useMemo(() => {
    const taskTemplates = data?.taskTemplates ?? []
    const result: SelectOption<TaskFieldOption>[] = []

    const sortedTemplates = [...taskTemplates].sort((a, b) =>
      (a.identifier ?? '').localeCompare(b.identifier ?? '')
    )

    for (const taskTemplate of sortedTemplates) {
      const groupName = taskTemplate.identifier || 'Unnamed Task'
      const fields = taskTemplate.fieldGroup?.fields ?? []

      const sortedFields = [...fields]
        .filter((f) => f.isApplicationField)
        .sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''))

      for (const taskField of sortedFields) {
        result.push({
          group: groupName,
          label: `${groupName}: ${taskField.label ?? ''}`,
          customDisplay: (v: TaskFieldOption) => v.label,
          value: { fieldId: taskField.id, label: taskField.label ?? '' },
        })
      }
    }

    return result
  }, [data?.taskTemplates])

  const selectedOption = useMemo(() => {
    const selectedId = field.state.value?.[0]
    if (!selectedId) {
      return undefined
    }
    return options.find((o) => o.value.fieldId === selectedId)
  }, [field.state.value, options])

  const getOptionKey = useCallback((o: SelectOption<TaskFieldOption>) => o.value.fieldId, [])

  const handleSelectedOptionChange = useCallback(
    (option: SelectOption<TaskFieldOption> | undefined) => {
      if (option) {
        field.setValue([option.value.fieldId])
        onFieldSelected?.(option.value.label)
      } else {
        field.setValue([])
      }
    },
    [field, onFieldSelected]
  )

  const errorMessage = field.state.meta.errors.join(', ')
  const isInvalid = errorMessage.length > 0

  return (
    <Select<TaskFieldOption>
      errorMessage={errorMessage}
      getOptionKey={getOptionKey}
      isDisabled={isLoading}
      isInvalid={isInvalid}
      isLoading={isLoading}
      isRequired
      label="Task Field"
      onBlur={field.handleBlur}
      onSelectedOptionChange={handleSelectedOptionChange}
      options={options}
      placeholder={isLoading ? 'Loading fields...' : 'Select a task field'}
      selectedOption={selectedOption}
    />
  )
}

export default React.memo(TaskFieldSelectField)
